import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

export default class extends Controller {
  static targets = [
    'modal',
    'promptInput',
    'inputState',
    'loadingState',
    'errorState',
    'successState',
    'statusMessage',
    'errorMessage'
  ]

  static values = {
    generateUrl: String
  }

  connect() {
    // Generate unique session ID for this page load
    this.sessionId = `bt_${Math.random().toString(36).substring(2, 15)}`
    this.subscription = null
    this.generationTimeout = null
    this.isConnected = false

    console.log('[BlockTemplateGenerator] Controller connected, session:', this.sessionId)
    console.log('[BlockTemplateGenerator] Generate URL:', this.generateUrlValue)
    console.log('[BlockTemplateGenerator] Consumer:', consumer)

    // Setup keyboard listener
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)

    // Setup ActionCable subscription
    this.setupSubscription()
  }

  disconnect() {
    document.removeEventListener('keydown', this.handleKeydown)
    this.clearGenerationTimeout()
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  setupSubscription() {
    console.log('[BlockTemplateGenerator] Setting up ActionCable subscription...')
    console.log('[BlockTemplateGenerator] Consumer connection state:', consumer?.connection?.isOpen?.())

    this.subscription = consumer.subscriptions.create(
      { channel: 'BlockTemplateGenerationChannel', session_id: this.sessionId },
      {
        connected: () => {
          console.log('[BlockTemplateGenerator] ActionCable connected')
          this.isConnected = true
        },
        disconnected: () => {
          console.log('[BlockTemplateGenerator] ActionCable disconnected')
          this.isConnected = false
        },
        rejected: () => {
          console.error('[BlockTemplateGenerator] ActionCable subscription rejected - admin auth issue?')
          this.isConnected = false
          // Don't show error immediately, only when generation is attempted
        },
        received: (data) => {
          console.log('[BlockTemplateGenerator] Received:', data)
          this.clearGenerationTimeout()
          this.handleWebSocketMessage(data)
        }
      }
    )
  }

  clearGenerationTimeout() {
    if (this.generationTimeout) {
      clearTimeout(this.generationTimeout)
      this.generationTimeout = null
    }
  }

  setGenerationTimeout() {
    this.clearGenerationTimeout()
    // 60 second timeout for AI generation
    this.generationTimeout = setTimeout(() => {
      console.error('[BlockTemplateGenerator] Generation timed out after 60 seconds')
      this.showErrorState('Generation timed out. The AI service may be busy - please try again.')
    }, 60000)
  }

  handleWebSocketMessage(data) {
    switch (data.event) {
      case 'generation_started':
        if (this.hasStatusMessageTarget) {
          this.statusMessageTarget.textContent = data.message || 'AI is generating...'
        }
        break

      case 'generation_completed':
        this.showSuccessState()
        this.fillFormWithTemplate(data.template)
        break

      case 'generation_failed':
        this.showErrorState(data.error || data.message || 'Generation failed')
        break
    }
  }

  handleKeydown(e) {
    if (e.key === 'Escape' && this.isModalOpen()) {
      this.closeModal()
    }
  }

  isModalOpen() {
    return this.hasModalTarget && !this.modalTarget.classList.contains('hidden')
  }

  // Actions
  openModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
      this.resetToInput()
      if (this.hasPromptInputTarget) {
        this.promptInputTarget.focus()
      }
    }
  }

  closeModal(event) {
    // Only close if clicking the backdrop itself, not the modal content
    if (event && event.target !== this.modalTarget) return

    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  forceCloseModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }
  }

  setPromptExample(event) {
    const text = event.currentTarget.dataset.example
    if (this.hasPromptInputTarget) {
      this.promptInputTarget.value = text
    }
  }

  // State transitions
  showInputState() {
    if (this.hasInputStateTarget) this.inputStateTarget.classList.remove('hidden')
    if (this.hasLoadingStateTarget) this.loadingStateTarget.classList.add('hidden')
    if (this.hasErrorStateTarget) this.errorStateTarget.classList.add('hidden')
    if (this.hasSuccessStateTarget) this.successStateTarget.classList.add('hidden')
  }

  showLoadingState() {
    if (this.hasInputStateTarget) this.inputStateTarget.classList.add('hidden')
    if (this.hasLoadingStateTarget) this.loadingStateTarget.classList.remove('hidden')
    if (this.hasErrorStateTarget) this.errorStateTarget.classList.add('hidden')
    if (this.hasSuccessStateTarget) this.successStateTarget.classList.add('hidden')
  }

  showErrorState(message) {
    if (this.hasInputStateTarget) this.inputStateTarget.classList.add('hidden')
    if (this.hasLoadingStateTarget) this.loadingStateTarget.classList.add('hidden')
    if (this.hasErrorStateTarget) this.errorStateTarget.classList.remove('hidden')
    if (this.hasSuccessStateTarget) this.successStateTarget.classList.add('hidden')
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
    }
  }

  showSuccessState() {
    if (this.hasInputStateTarget) this.inputStateTarget.classList.add('hidden')
    if (this.hasLoadingStateTarget) this.loadingStateTarget.classList.add('hidden')
    if (this.hasErrorStateTarget) this.errorStateTarget.classList.add('hidden')
    if (this.hasSuccessStateTarget) this.successStateTarget.classList.remove('hidden')
  }

  resetToInput() {
    this.showInputState()
    if (this.hasPromptInputTarget) {
      this.promptInputTarget.value = ''
    }
  }

  // Start generation
  async startGeneration() {
    const prompt = this.hasPromptInputTarget ? this.promptInputTarget.value.trim() : ''

    if (!prompt) {
      alert('Please enter a description for your block template')
      return
    }

    console.log('[BlockTemplateGenerator] Starting generation...')
    console.log('[BlockTemplateGenerator] Connection state:', this.isConnected)
    console.log('[BlockTemplateGenerator] Session ID:', this.sessionId)

    this.showLoadingState()
    this.setGenerationTimeout()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      console.log('[BlockTemplateGenerator] CSRF token present:', !!csrfToken)
      console.log('[BlockTemplateGenerator] Posting to:', this.generateUrlValue)

      const response = await fetch(this.generateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          prompt: prompt
        })
      })

      console.log('[BlockTemplateGenerator] Response status:', response.status)
      const data = await response.json()
      console.log('[BlockTemplateGenerator] Response data:', data)

      if (!response.ok) {
        this.clearGenerationTimeout()
        this.showErrorState(data.error || 'Failed to start generation')
      } else {
        console.log('[BlockTemplateGenerator] Job queued, waiting for WebSocket response...')
        // Update status message
        if (this.hasStatusMessageTarget) {
          this.statusMessageTarget.textContent = 'Job queued - AI is generating your template...'
        }
      }
    } catch (error) {
      console.error('[BlockTemplateGenerator] Generation request failed:', error)
      this.clearGenerationTimeout()
      this.showErrorState(`Failed to connect to server: ${error.message}`)
    }
  }

  // Fill form with generated template
  fillFormWithTemplate(template) {
    if (!template) return

    // Fill basic fields
    const fields = {
      'website_block_template_name': template.name,
      'website_block_template_block_type': template.block_type,
      'website_block_template_category': template.category,
      'website_block_template_description': template.description,
      'website_block_template_icon': template.icon,
      'website_block_template_position': template.position || 0
    }

    Object.entries(fields).forEach(([id, value]) => {
      const field = document.getElementById(id)
      if (field && value !== undefined) {
        field.value = value
      }
    })

    // Fill JSON fields with pretty formatting
    const jsonFields = {
      'website_block_template_default_content': template.default_content,
      'website_block_template_default_settings': template.default_settings,
      'website_block_template_schema': template.schema
    }

    Object.entries(jsonFields).forEach(([id, value]) => {
      const field = document.getElementById(id)
      if (field && value) {
        field.value = JSON.stringify(value, null, 2)
      }
    })

    // Flash the form fields to indicate they were filled
    document.querySelectorAll('input, textarea, select').forEach((field) => {
      field.classList.add('ring-2', 'ring-green-500', 'ring-opacity-50')
      setTimeout(() => {
        field.classList.remove('ring-2', 'ring-green-500', 'ring-opacity-50')
      }, 2000)
    })
  }
}
